import { computed, ref, type Ref } from 'vue';
import { store } from '@/plugins/store';

/** What the reader asked for that would lose what was typed: another farm, or closing the dialog. */
export type UnsavedInputAction = { kind: 'switchFarm'; farmId: number | null } | { kind: 'close' };

/**
 * Guards what was typed into a bulk count or purchase: a change of farm or closing the dialog would lose it,
 * so while something is typed either one waits for the reader to confirm, and a clean dialog acts at once.
 *
 * Closing after a save sets `isOpen` directly and is not asked about.
 */
export function useUnsavedInputGuard(farmId: Ref<number | null>, isOpen: Ref<boolean>, isDirty: Ref<boolean>) {
    /** The action waiting for the reader to confirm losing what was typed. */
    const pendingAction = ref<UnsavedInputAction | null>(null);

    /** The farm whose inputs would be lost, named in the warning. */
    const currentFarmName = computed(() => store.state.farms.find((farm) => farm.farm_id === farmId.value)?.name ?? '');

    function perform(action: UnsavedInputAction) {
        if (action.kind === 'switchFarm') {
            farmId.value = action.farmId;
        } else {
            isOpen.value = false;
        }
    }

    function request(action: UnsavedInputAction) {
        if (isDirty.value) {
            pendingAction.value = action;
        } else {
            pendingAction.value = null;
            perform(action);
        }
    }

    function requestFarm(id: number | null) {
        if (id === farmId.value) {
            pendingAction.value = null;
        } else {
            request({ kind: 'switchFarm', farmId: id });
        }
    }

    /** The dialog's open state as the reader changes it: opening goes through, closing is asked about. */
    function requestOpen(open: boolean) {
        if (open) {
            isOpen.value = true;
        } else {
            request({ kind: 'close' });
        }
    }

    function confirm() {
        const action = pendingAction.value;

        pendingAction.value = null;
        if (action) {
            perform(action);
        }
    }

    function keep() {
        pendingAction.value = null;
    }

    return { pendingAction, currentFarmName, requestFarm, requestOpen, confirm, keep };
}
